import { SURAHS } from './surahs'
import type { Surah, Ayah, Translation, Edition } from '../types'

// Direct-to-CloudFront reader. Static JSON lives under /quran/* on the frontend domain
// (served by CloudFront cache behavior from the assets S3 bucket). No Lambda involved,
// no auth required. Same pattern as wordApi.ts and reciters.ts.
const ASSETS_BASE = import.meta.env.VITE_APP_URL || ''
const CACHE_SIZE = 20

// Explicitly-downloaded text/translations live in this Cache Storage namespace
// (written by offlineStore.downloadSurah). Read it DIRECTLY — same pattern and
// rationale as audioFetcher.ts — so a downloaded surah is served locally even
// when ONLINE, and still works when the service worker is absent (first visit,
// private mode, SW registration failure). Keep the name in sync with
// offlineStore.ts CACHE_QURAN and the SW rule in vite.config.ts.
const OFFLINE_QURAN_CACHE = 'qlearn-offline-quran-v1'

async function offlineCachedResponse(path: string): Promise<Response | null> {
  if (typeof caches === 'undefined') return null
  try {
    const cache = await caches.open(OFFLINE_QURAN_CACHE)
    // Match on the RELATIVE path: offlineStore.putInCache keys entries by the
    // relative URL ('/quran/text/2.json'), which the Cache API resolves
    // against the page origin — NOT by `${ASSETS_BASE}${path}`. Probing the
    // fetch URL would miss every entry whenever VITE_APP_URL is set.
    const hit = await cache.match(path, { ignoreVary: true })
    return hit && hit.ok ? hit : null
  } catch {
    return null
  }
}

interface SurahTextFile {
  surahNumber: number
  numberOfAyahs: number
  ayahs: Array<{
    number: number
    ayahNumber: number
    textUthmani: string
    textSimple: string
    juz: number
    page: number
    hizbQuarter?: number
    ruku?: number
    manzil?: number
    sajda?: boolean
  }>
}

interface SurahTranslationFile {
  surahNumber: number
  edition: string
  ayahs: Array<{ ayahNumber: number; text: string }>
}

// LRU caches (insertion-order Map, same pattern as wordApi)
const textCache = new Map<number, SurahTextFile>()
const translationCache = new Map<string, SurahTranslationFile>()
let surahsListCache: Surah[] | null = null
let editionsListCache: Edition[] | null = null

function lruGet<K, V>(cache: Map<K, V>, key: K): V | undefined {
  const value = cache.get(key)
  if (value !== undefined) {
    cache.delete(key)
    cache.set(key, value)
  }
  return value
}

function lruSet<K, V>(cache: Map<K, V>, key: K, value: V): void {
  if (cache.has(key)) {
    cache.delete(key)
  } else if (cache.size >= CACHE_SIZE) {
    const oldest = cache.keys().next().value
    if (oldest !== undefined) cache.delete(oldest)
  }
  cache.set(key, value)
}

async function fetchJSON<T>(path: string): Promise<T> {
  // Downloaded copy first (even when online), network fallback on a miss.
  // The `??` replaces (not precedes) the fetch, so there's no double read;
  // non-downloaded content hits the network exactly as before.
  const response = (await offlineCachedResponse(path)) ?? (await fetch(`${ASSETS_BASE}${path}`))
  if (!response.ok) {
    throw new Error(`Failed to fetch ${path}: ${response.status}`)
  }
  return response.json()
}

async function fetchSurahText(surahNumber: number): Promise<SurahTextFile> {
  const cached = lruGet(textCache, surahNumber)
  if (cached) return cached
  const data = await fetchJSON<SurahTextFile>(`/quran/text/${surahNumber}.json`)
  lruSet(textCache, surahNumber, data)
  return data
}

async function fetchTranslation(surahNumber: number, edition: string): Promise<SurahTranslationFile> {
  const key = `${edition}:${surahNumber}`
  const cached = lruGet(translationCache, key)
  if (cached) return cached
  const data = await fetchJSON<SurahTranslationFile>(`/quran/translations/${edition}/${surahNumber}.json`)
  lruSet(translationCache, key, data)
  return data
}

export const quranApi = {
  async listSurahs(): Promise<Surah[]> {
    if (surahsListCache) return surahsListCache
    try {
      surahsListCache = await fetchJSON<Surah[]>('/quran/surahs.json')
    } catch {
      // Offline and /quran/surahs.json not in any cache (it's deliberately
      // excluded from the pinned offline cache). getSurahWithTranslation
      // Promise.all's this list, so without a fallback a fully-downloaded
      // surah would still fail to render. The bundled static list has the
      // full Surah shape and is already the offline store's source of truth.
      surahsListCache = SURAHS
    }
    return surahsListCache
  },

  async listEditions(type?: string): Promise<Edition[]> {
    if (!editionsListCache) {
      editionsListCache = await fetchJSON<Edition[]>('/quran/editions.json')
    }
    return type ? editionsListCache.filter((e) => e.type === type) : editionsListCache
  },

  async getSurahWithTranslation(
    surahNumber: number,
    edition: string
  ): Promise<{ surah: Surah; ayahs: Ayah[]; translations: Translation[] }> {
    const [surahsList, text, trans] = await Promise.all([
      this.listSurahs(),
      fetchSurahText(surahNumber),
      fetchTranslation(surahNumber, edition),
    ])

    const surah = surahsList.find((s) => s.number === surahNumber)
    if (!surah) {
      throw new Error(`Surah ${surahNumber} not found`)
    }

    // Defensive sort by ayahNumber — the source JSON should be in order, but
    // playback assumes sequential ordering. Out-of-order entries would cause
    // audio + scroll handoff bugs and would be hard to spot from a glance.
    const ayahs: Ayah[] = text.ayahs
      .map((a) => ({
        number: a.number,
        surahNumber,
        ayahNumber: a.ayahNumber,
        text: a.textUthmani,
        textUthmani: a.textUthmani,
        textSimple: a.textSimple,
        juz: a.juz,
        page: a.page,
      }))
      .sort((x, y) => x.ayahNumber - y.ayahNumber)

    const translations: Translation[] = trans.ayahs
      .map((t) => ({
        surahNumber,
        ayahNumber: t.ayahNumber,
        edition,
        text: t.text,
      }))
      .sort((x, y) => x.ayahNumber - y.ayahNumber)

    return { surah, ayahs, translations }
  },
}
